import { Box, Card, CardContent, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { PaymentMethodEnum } from 'enums';
import Image from 'next/image';
import { COLOR_GRAY_90, COLOR_PURPLE } from 'styles/variables/colors';
import { europeanCountries } from 'utils/countries';
import ExternalPaymentContent from './ExternalPaymentContent';
import SEPADebitContent from './SEPADebitContent';
import CreditCardContent from './CreditCardContent';
import { useCheckoutContext } from 'contexts/CheckoutContext';

const useStyles = makeStyles((theme) => ({
  paymentMethodTitle: {
    fontFamily: 'Lato',
    color: COLOR_GRAY_90,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '120%',
  },
  paymentDetailsCard: {
    marginTop: theme.spacing(2),
    boxShadow: 'none !important',

    '& .MuiCardContent-root': {
      padding: '0 !important',
    },

    '& label, fieldset': {
      color: '#616161',
      borderColor: '#616161',
    },

    '& label': {
      color: '#616161 !important',
    },
  },
  paymentMethods: {
    gap: '16px',

    '& > button': {
      flex: '0 0 48%',
    },

    '& .active': {
      border: `2px solid ${COLOR_PURPLE}`,
    },

    [theme.breakpoints.down('lg')]: {
      gap: '12px',
    },
  },
  paymentBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    gap: 8,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '122%',
    borderRadius: 4,
    border: '1px solid #BDBDBD',
    backgroundColor: '#FFFFFF',

    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

const paymentMethods = [
  {
    id: PaymentMethodEnum.CARD,
    label: 'Credit Card',
    active: true,
    img: {
      src: '/images/creditcard-icon.svg',
      alt: 'Credit card icon',
      width: 24,
      height: 24,
    },
    content: {
      id: 'credit-card',
      component: CreditCardContent,
    },
  },
  {
    id: PaymentMethodEnum.PAYPAL,
    active: false,
    img: {
      src: '/images/paypal-logo.svg',
      alt: 'PayPal logo',
      width: 92,
      height: 24,
    },
    content: {
      id: 'external-payment',
      component: ExternalPaymentContent,
      props: { methodLabel: 'PayPal' },
    },
    shouldHide: ({ countryCode }) => {
      const paypalEnabledCountries = ['US', 'BR'];
      return !paypalEnabledCountries.includes(countryCode);
    },
  },
  {
    id: PaymentMethodEnum.ALIPAY,
    active: false,
    img: {
      src: '/images/alipay-logo.png',
      alt: 'Alipay logo',
      width: 92,
      height: 28,
    },
    content: {
      id: 'external-payment',
      component: ExternalPaymentContent,
      props: { methodLabel: 'Alipay', isSingleUse: true },
    },
  },
  {
    id: PaymentMethodEnum.SEPA_DEBIT,
    active: true,
    img: {
      src: '/images/sepa-new-logo.png',
      alt: 'SEPA Debit logo',
      width: 82,
      height: 22,
      props: {
        objectFit: 'contain',
      },
    },
    shouldHide: ({ countryCode, currentPlan }) => {
      const isEurope = europeanCountries.includes(countryCode);
      return !(isEurope && (!currentPlan || currentPlan.currency === 'EUR'));
    },
    content: {
      id: 'sepa',
      component: SEPADebitContent,
    },
  },
  {
    id: PaymentMethodEnum.WECHAT_PAY,
    active: false,
    img: {
      src: '/images/wechat-pay-logo.svg',
      alt: 'WeChat Pay logo',
      width: 94,
      height: 28,
    },
    content: {
      id: 'external-payment',
      component: ExternalPaymentContent,
      props: { methodLabel: 'WeChat Pay', isSingleUse: true },
    },
  },
];

export default function PaymentMethods() {
  const classes = useStyles();
  const {
    data: {
      paymentMethod: activePaymentMethod,
      currentPlan,
      isMobile,
      user: { countryCode },
    },
    actions: { onPaymentMethodChange },
  } = useCheckoutContext();

  const getMethodContent = (method) => {
    const methodData = paymentMethods.find((m) => m.id === method)?.content;

    if (!methodData) {
      return null;
    }

    const { component: Component } = methodData;

    return Component ? <Component {...methodData.props} /> : null;
  };

  return (
    <Box>
      <Typography className={classes.paymentMethodTitle}>Payment method</Typography>

      <Card className={classes.paymentDetailsCard}>
        <Box
          justifyContent={!isMobile && 'space-between'}
          display={!isMobile ? 'flex' : 'grid'}
          flexWrap="wrap"
          mb={4}
          className={classes.paymentMethods}
        >
          {paymentMethods.map((method) => {
            const hide = method.shouldHide && method.shouldHide({ countryCode, currentPlan });
            if (hide || !method.active) {
              return null;
            }

            return (
              <button
                key={method.id}
                className={clsx(classes.paymentBox, method.id === activePaymentMethod && 'active')}
                onClick={onPaymentMethodChange}
                method={method.id}
              >
                <Image
                  src={method.img.src}
                  alt={method.img.alt}
                  width={method.img.width}
                  height={method.img.height}
                  loading="lazy"
                  {...method.img.props}
                />
                {method.label && <span>{method.label}</span>}
              </button>
            );
          })}
        </Box>

        <CardContent id="form-section">{getMethodContent(activePaymentMethod)}</CardContent>
      </Card>
    </Box>
  );
}
